import React, { useState, useRef, useEffect } from "react";
import { Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import api from "../../Utils/api";

import AddShiftsFromFileDialog from "./AddShiftsFromFileDialog";

const FIELD_OPTIONS = [
  { value: "startTime", label: "Start Time", type: "time" },
  { value: "endTime", label: "End Time", type: "time" },
  {
    value: "breakStartTime",
    label: "Break Start Time",
    type: "time",
  },
  { value: "breakEndTime", label: "Break End Time", type: "time" },
  { value: "locationID", label: "Location", type: "select" },
  { value: "comment", label: "Comment", type: "text" },
];

const OptionsDropdown = ({
  selectedDate,
  period,
  getSchedules,
  locations,
  shiftCount,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [selectedField, setSelectedField] = useState("");
  const [fieldValue, setFieldValue] = useState("");

  const [showAddFileDialog, setShowAddFileDialog] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const deleteAllShifts = async () => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete all shifts from ${selectedDate.toDateString()} for ${period} day(s)?`
    );

    if (!confirmDelete) return;

    const start = new Date(selectedDate); // selectedDate should be a Date object
    start.setUTCHours(0, 0, 0, 0); // Set to 00:00:00.000

    const end = new Date(start); // clone the start date
    end.setDate(end.getDate() + period - 1); // add (period - 1) days to include start date
    end.setUTCHours(23, 59, 59, 999); // Set to 23:59:59.999

    const res = await api.post(`/schedule/delete_list`, {
      startTime: start.toISOString(),
      endTime: end.toISOString(),
      admin: true,
    });

    if (res.data.success) {
      toast.success(res.data.message);
      getSchedules();
    } else {
      toast.error("Failed to delete!");
    }
  };

  const markAllShiftsEmpty = async () => {
    const start = new Date(selectedDate); // selectedDate should be a Date object
    start.setUTCHours(0, 0, 0, 0); // Set to 00:00:00.000

    const end = new Date(start); // clone the start date
    end.setDate(end.getDate() + period - 1); // add (period - 1) days to include start date
    end.setUTCHours(23, 59, 59, 999); // Set to 23:59:59.999

    const res = await api.post(`/schedule/make_all_empty`, {
      startTime: start.toISOString(), // full ISO with time
      endTime: end.toISOString(), // full ISO with time
      admin: true,
    });

    if (res.data.success) {
      toast.success(res.data.message);
      getSchedules();
    } else {
      toast.error("Failed to delete!");
    }
  };

  const removeAllEmptyShifts = async () => {
    const start = new Date(selectedDate); // selectedDate should be a Date object
    start.setUTCHours(0, 0, 0, 0); // Set to 00:00:00.000

    const end = new Date(start); // clone the start date
    end.setDate(end.getDate() + period - 1); // add (period - 1) days to include start date
    end.setUTCHours(23, 59, 59, 999); // Set to 23:59:59.999

    const res = await api.post(`/schedule/remove_all_empty`, {
      startTime: start.toISOString(), // full ISO with time
      endTime: end.toISOString(), // full ISO with time
      admin: true,
    });

    if (res.data.success) {
      toast.success(res.data.message);
      getSchedules();
    } else {
      toast.error("Failed to delete!");
    }
  };

  const markAllEmptyToOpen = async () => {
    const start = new Date(selectedDate); // selectedDate should be a Date object
    start.setUTCHours(0, 0, 0, 0); // Set to 00:00:00.000

    const end = new Date(start); // clone the start date
    end.setDate(end.getDate() + period - 1); // add (period - 1) days to include start date
    end.setUTCHours(23, 59, 59, 999); // Set to 23:59:59.999

    const res = await api.post(`/schedule/make_all_open`, {
      startTime: start.toISOString(), // full ISO with time
      endTime: end.toISOString(), // full ISO with time
      admin: true,
    });

    if (res.data.success) {
      toast.success(res.data.message);
      getSchedules();
    } else {
      toast.error("Failed to delete!");
    }
  };

  const handleBulkUpdate = async () => {
    // Implement your API call here

    const start = new Date(selectedDate); // selectedDate should be a Date object
    start.setUTCHours(0, 0, 0, 0); // Set to 00:00:00.000

    const end = new Date(start); // clone the start date
    end.setDate(end.getDate() + period - 1); // add (period - 1) days to include start date
    end.setUTCHours(23, 59, 59, 999); // Set to 23:59:59.999

    const res = await api.post(`/schedule/bulk_update`, {
      startTime: start.toISOString(), // full ISO with time
      endTime: end.toISOString(), // full ISO with time
      type: selectedField,
      value: fieldValue,
      admin: true,
    });

    if (res.data.success) {
      toast.success(res.data.message);
      getSchedules();
    } else {
      toast.error("Failed to delete!");
    }

    setShowBulkDialog(false);
    getSchedules();
  };

  // Render the right input based on selected field
  const renderFieldInput = () => {
    const field = FIELD_OPTIONS.find((f) => f.value === selectedField);
    if (!field) {
      return (
        <input
          className="w-full border rounded px-3 py-2"
          disabled
          placeholder=""
        />
      );
    }
    if (field.type === "select") {
      return (
        <select
          className="w-full border rounded px-3 py-2"
          value={fieldValue}
          onChange={(e) => setFieldValue(e.target.value)}
        >
          <option value="">Select location</option>
          {locations.map((loc, i) => (
            <option key={i} value={loc._id}>
              {loc.name}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        type={field.type}
        className="w-full border rounded px-3 py-2"
        value={fieldValue}
        onChange={(e) => setFieldValue(e.target.value)}
        placeholder={field.label}
      />
    );
  };

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen((open) => !open)}
        className="inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
      >
        <Settings className="mr-2 w-4 h-4" />
        <span className="md:hidden lg:block">Options</span>
      </button>
      {isOpen && (
        <div className="absolute right-0 z-20 mt-2 w-60 bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="py-1">
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                setShowAddFileDialog(true);
                setIsOpen(false);
              }}
            >
              Add shifts from file
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                markAllShiftsEmpty();
                setIsOpen(false);
              }}
            >
              Mark all shifts empty
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                removeAllEmptyShifts();
                setIsOpen(false);
              }}
            >
              Remove empty shifts
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                deleteAllShifts();
                setIsOpen(false);
              }}
            >
              Delete all shifts
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                markAllEmptyToOpen();
                setIsOpen(false);
              }}
            >
              Mark empty shifts as open
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                setShowBulkDialog(true);
                setIsOpen(false);
              }}
            >
              Bulk update
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => {
                setIsOpen(false);
                navigate("/locations");
              }}
            >
              Edit locations and areas
            </button>
          </div>
        </div>
      )}
      {/* Bulk Update Dialog */}
      {showBulkDialog && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-xl shadow-lg p-6 w-[430px]">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                Bulk Update {shiftCount} shifts
              </h2>
              <button
                className="text-gray-400 hover:text-gray-700 text-2xl font-bold"
                onClick={() => setShowBulkDialog(false)}
              >
                &times;
              </button>
            </div>
            <div className="mb-4 text-sm text-gray-600">
              This change will apply to all shifts visible.
            </div>
            <div className="flex gap-2 mb-6">
              <select
                className="w-1/2 border rounded px-3 py-2"
                value={selectedField}
                onChange={(e) => {
                  setSelectedField(e.target.value);
                  setFieldValue("");
                }}
              >
                <option value="">Choose Field</option>
                {FIELD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div className="w-1/2">{renderFieldInput()}</div>
            </div>
            <div className="flex justify-end">
              <button
                className="bg-gray-700 text-white px-5 py-2 rounded font-semibold"
                onClick={handleBulkUpdate}
                disabled={!selectedField || !fieldValue}
              >
                Apply Update!
              </button>
            </div>
          </div>
        </div>
      )}
      {/* // In your parent component: const [showDialog, setShowDialog] =
      React.useState(false); */}
      <AddShiftsFromFileDialog
        getSchedules={getSchedules}
        open={showAddFileDialog}
        onClose={() => setShowAddFileDialog(false)}
        onDownload={() => {
          /* handle template download */
        }}
        onContinue={() => {
          /* handle continue/upload */
        }}
        onViewHistory={() => {
          /* handle view import history */
        }}
      />
    </div>
  );
};

export default OptionsDropdown;
